import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListSegmentsResult } from '@whinerp/client';
import type { Block, BlockDocument, ShowWhen } from '@whinerp/contracts/blocks';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * Who sees each block (G-178): everybody, or only people with a field value,
 * in an audience, or who clicked an email lately. The unsubscribe footer is
 * not listed - everybody sees it.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-meta text-ink';
const FIELDS = ['country', 'city', 'company', 'title', 'language'] as const;

type Kind = 'everyone' | ShowWhen['kind'];

function starting(kind: Kind, segments: ListSegmentsResult['segments']): ShowWhen | undefined {
  if (kind === 'field') return { kind: 'field', field: 'country', operator: 'eq', value: '' };
  if (kind === 'segment') return { kind: 'segment', segmentId: segments[0]?.segmentId ?? '', member: true };
  if (kind === 'engaged') return { kind: 'engaged', days: 30, engaged: true };
  return undefined;
}

export function BlockAudiencePanel({ document, onChange }: { document: BlockDocument; onChange: (next: BlockDocument) => void }): JSX.Element | null {
  const t = useT();
  const segments = useQuery<ListSegmentsResult, ApiError>({ queryKey: ['segments'], queryFn: () => api.listSegments({}) });
  const blocks = document.blocks.filter((block) => block.type !== 'unsubscribe' && block.type !== 'divider' && block.type !== 'spacer');
  if (blocks.length === 0) return null;
  const available = segments.data?.segments ?? [];

  const set = (id: string, showWhen: ShowWhen | undefined) =>
    onChange({
      blocks: document.blocks.map((block): Block => {
        if (block.id !== id) return block;
        const { showWhen: _previous, ...rest } = block;
        return showWhen ? { ...rest, showWhen } : rest;
      }),
    });

  return (
    <Panel data-region="block-audience">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('dynamic.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('dynamic.help')}</p>
        <ul className="flex flex-col gap-2">
          {blocks.map((block) => {
            const rule = block.showWhen;
            const kind: Kind = rule?.kind ?? 'everyone';
            return (
              <li key={block.id} data-block-audience={block.id} className="flex flex-wrap items-center gap-2 text-dense">
                <span className="min-w-0 max-w-64 truncate text-ink">{block.text?.trim() || t(`dynamic.block.${block.type}` as 'dynamic.block.text')}</span>
                <select className={INPUT} data-block-audience-kind aria-label={t('dynamic.who')} value={kind}
                  onChange={(event) => set(block.id, starting(event.target.value as Kind, available))}>
                  <option value="everyone">{t('dynamic.kind.everyone')}</option>
                  <option value="field">{t('dynamic.kind.field')}</option>
                  <option value="segment" disabled={available.length === 0}>{t('dynamic.kind.segment')}</option>
                  <option value="engaged">{t('dynamic.kind.engaged')}</option>
                </select>
                {rule?.kind === 'field' ? (
                  <>
                    <select className={INPUT} data-block-audience-field aria-label={t('dynamic.field')} value={rule.field}
                      onChange={(event) => set(block.id, { ...rule, field: event.target.value as (typeof FIELDS)[number] })}>
                      {FIELDS.map((field) => <option key={field} value={field}>{t(`dynamic.field.${field}`)}</option>)}
                    </select>
                    <select className={INPUT} data-block-audience-operator aria-label={t('dynamic.operator')} value={rule.operator}
                      onChange={(event) => set(block.id, { ...rule, operator: event.target.value as 'eq' })}>
                      {(['eq', 'neq', 'set', 'unset'] as const).map((operator) => <option key={operator} value={operator}>{t(`dynamic.operator.${operator}`)}</option>)}
                    </select>
                    {rule.operator === 'eq' || rule.operator === 'neq' ? (
                      <input className={`${INPUT} w-28`} data-block-audience-value aria-label={t('dynamic.value')} value={rule.value ?? ''}
                        onChange={(event) => set(block.id, { ...rule, value: event.target.value })} />
                    ) : null}
                  </>
                ) : null}
                {rule?.kind === 'segment' ? (
                  <>
                    <select className={INPUT} data-block-audience-member aria-label={t('dynamic.inOrOut')} value={rule.member ? 'in' : 'out'}
                      onChange={(event) => set(block.id, { ...rule, member: event.target.value === 'in' })}>
                      <option value="in">{t('dynamic.inSegment')}</option>
                      <option value="out">{t('dynamic.notInSegment')}</option>
                    </select>
                    <select className={INPUT} data-block-audience-segment aria-label={t('dynamic.kind.segment')} value={rule.segmentId}
                      onChange={(event) => set(block.id, { ...rule, segmentId: event.target.value })}>
                      {available.map((segment) => <option key={segment.segmentId} value={segment.segmentId}>{segment.name}</option>)}
                    </select>
                  </>
                ) : null}
                {rule?.kind === 'engaged' ? (
                  <>
                    <select className={INPUT} data-block-audience-engaged aria-label={t('dynamic.kind.engaged')} value={rule.engaged ? 'yes' : 'no'}
                      onChange={(event) => set(block.id, { ...rule, engaged: event.target.value === 'yes' })}>
                      <option value="yes">{t('dynamic.clicked')}</option>
                      <option value="no">{t('dynamic.notClicked')}</option>
                    </select>
                    <input className={`${INPUT} w-16`} type="number" min={1} max={365} data-block-audience-days aria-label={t('dynamic.days')} value={rule.days}
                      onChange={(event) => set(block.id, { ...rule, days: Number(event.target.value) })} />
                    <span className="text-ink-meta">{t('dynamic.days')}</span>
                  </>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </Panel>
  );
}
